<template>
  <div class="bg-card text-left">
    <div class="d-flex justify-content-between">
      <div>
        <strong>{{ $t('pages.one-company.company-control.company-data') }}</strong>
      </div>
      <div v-if="company.company.isVerified && company.company.status === 'active'">
        <b-button
          v-b-modal.modal-reject
          variant="warning"
          class="status-change-btn"
        >
          {{ $t('generic.reject', 'Reject') }}
        </b-button>
      </div>
      <div v-if="!company.company.isVerified && company.company.status === 'pending'">
        <b-button
          v-b-modal.modal-accept
          variant="success"
          class="status-change-btn"
        >
          {{ $t('generic.confirm', 'Confirm') }}
        </b-button>
        <b-button
          v-b-modal.modal-reject
          variant="warning"
          class="status-change-btn"
        >
          {{ $t('generic.reject', 'Reject') }}
        </b-button>
      </div>
      <div v-else-if="!company.company.isVerified && company.company.status === 'rejected'">
        <b-button
          v-b-modal.modal-accept
          variant="success"
          class="status-change-btn"
        >
          {{ $t('generic.confirm', 'Confirm') }}
        </b-button>
        <b-button
          v-b-modal.modal-delete
          variant="danger"
          class="status-change-btn"
        >
          {{ $t('generic.delete', 'Delete') }}
        </b-button>
      </div>
    </div>
    <hr>
    <div>
      <div v-if="company">
        <div class="row">
          <div class="col-12 col-md-6">
            <h1 class="title">
              {{ company.company.title }}
            </h1>
            <div class="cif">
              <span>{{ $t('generic.cif', 'CIF') }}: </span>
              {{ company.company.number }}
            </div>
            <h1 class="contact">
              <font-awesome-icon
                icon="phone-square"
              />
              <span style="font-size: 0.9rem; color: #777"> {{ $t('generic.phone', 'Phone') }}:</span>
              {{ company.company.phone }}
            </h1>
            <div class="description">
              {{ company.company.description }}
            </div>
            <div class="sm-text">
              <font-awesome-icon
                icon="clock"
              />
              {{ company.created_at }}
            </div>
          </div>
          <div class="col-12 col-md-6 text-right">
            <div class="status">
              {{ $t('generic.status', 'Status') }}:
              <span :class="company.company.status.toLowerCase()">
                {{ translateStatus(company.company.status) }}
              </span>
            </div>
            <div class="mt-3">
              <div class="font-weight-bold mb-1">
                {{ $t('pages.one-company.company-control.adminNote.title', 'Private Note:') }}
              </div>
              <div style="font-size: 0.9rem;">
                <span v-if="!company.company.adminNote">{{ $t('pages.one-company.company-control.adminNote.no-note', 'No note yet...') }}</span>
                <span v-else>
                  {{ company.company.adminNote }}
                </span>
              </div>
              <b-button
                v-b-modal.modal-private-note
                size="sm"
                class="mt-2"
                style="padding: 1px 5px;"
              >
                {{ company.company.adminNote ? $t('pages.one-company.company-control.adminNote.btnEdit', 'Edit Note')
                  : $t('pages.one-company.company-control.adminNote.btnAdd', 'Add Note') }}
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MODALS -->
    <b-modal
      id="modal-accept"
      centered
      header-bg-variant="warning"
      :title="$t('generic.warning', 'Warning')"
      :ok-title="$t('generic.accept', 'Accept')"
      :cancel-title="$t('generic.cancel', 'Cancel')"
      @ok="verifyCompany()"
    >
      <!-- eslint-disable-next-line -->
      <p class="my-4" v-html="$t('pages.one-company.company-control.confirm')"></p>
    </b-modal>
    <b-modal
      id="modal-reject"
      centered
      header-bg-variant="warning"
      :title="$t('generic.warning', 'Warning')"
      :ok-title="$t('generic.accept', 'Accept')"
      :cancel-title="$t('generic.cancel', 'Cancel')"
      @ok="rejectCompany()"
    >
      <!-- eslint-disable-next-line -->
      <p class="mt-4 mb-1" v-html="$t('pages.one-company.company-control.reject')"></p>
    </b-modal>
    <b-modal
      id="modal-delete"
      centered
      header-bg-variant="danger"
      :title="$t('generic.warning', 'Warning')"
      @ok="deleteUser()"
    >
      <!-- eslint-disable-next-line -->
      <p class="mt-4 mb-1" v-html="$t('pages.one-company.company-control.delete')">
      </p>
      <p class="mb-3 text-danger text-center">
        {{ $t('pages.one-company.company-control.undo') }}
      </p>
    </b-modal>

    <!-- Private note modal -->
    <b-modal
      id="modal-private-note"
      centered
      :title="$t('pages.one-company.company-control.adminNote.modal.title', 'Modify Private Note')"
      :ok-title="$t('generic.save', 'Save')"
      :cancel-title="$t('generic.cancel', 'Cancel')"
      @ok="savePrivateNote()"
      @hidden="privateNoteModalClosed()"
    >
      <b-form-group
        id="private-note-group"
        class="col"
        :label="$t('pages.one-company.company-control.adminNote.modal.formLabel', 'Enter private note:')"
        label-for="title"
        :invalid-feedback="$t('pages.one-company.company-control.adminNote.modal.formValidation', 'Maximum 1000 characters!')"
      >
        <b-form-textarea
          id="private-note"
          v-model="privateNote"
          rows="6"
          :state="privateNote.length <= 1000"
        />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
export default {
  props: {
    company: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      privateNote: ""
    }
  },
  mounted() {
    if(this.company && this.company.company)
      this.privateNote = this.company.company.adminNote;
  },
  methods: {
    translateStatus() {
      if (this.company.company.status === 'pending') {
          return this.$t('generic.pending', 'Pending');
      } else if (this.company.company.status === 'rejected') {
          return this.$t('generic.rejected', 'Rejected');
      } else if (this.company.company.status === 'active') {
          return this.$t('generic.confirmed', 'Confirmed');
      } else {
          return this.company.company.status;
      }
    },
    verifyCompany() {
      this.axios.put('/admin/company/verify/' + this.company.id)
        .then( response => {
          this.company.company = response.data;
          this.$toastNotification('success', '', this.$t('pages.admin.companies.verify-success', 'Company verified successfully!'));
        })
        .catch(() => {
          this.$toastSomethingWentWrong();
        });
    },
    rejectCompany() {
      this.axios.put('/admin/company/reject/' + this.company.id)
        .then( response => {
          this.company.company = response.data;
          this.$toastNotification('success', '', this.$t('pages.admin.companies.reject-success', 'Company rejected successfully.'));
        })
        .catch(() => {
          this.$toastSomethingWentWrong();
        });
    },
    deleteUser() {
      this.axios.delete('/users/' + this.company.id)
        .then( () => {
          this.$router.push({ name: 'admin-companies' })
        })
        .catch( () => {
          this.$toastSomethingWentWrong();
        });
    },
    savePrivateNote() {
      this.axios.post('/admin/company/privateNote/' + this.company.id, { privateNote: this.privateNote })
        .then( response => {
          this.company.company = response.data;
          if (!this.company.company.adminNote) {
            this.company.company.adminNote = "";
          }
          this.privateNote = response.data.adminNote || "";
          this.$toastNotification("success", "", "Private note saved successfully"); // TODO: translate
        })
        .catch( () => {
          this.$toastSomethingWentWrong();
        });
    },
    privateNoteModalClosed() {
      this.privateNote = this.company.company.adminNote;
    }
  }
}
</script>

<style scoped lang="scss">
  .status-change-btn {
    padding: 3px 10px;
    font-size: 0.9rem;
    font-weight: 600;
    margin: 0 3px;
    text-transform: uppercase;
  }

  h1.name {
    font-size: 1.5rem !important;
  }

  h1.title {
    font-size: 1.4rem !important;
    color: #009090;
    text-transform: uppercase;
    margin: 0;
  }

  h1.contact {
    color: rgb(80, 80, 80);
    font-size: 1.2rem !important;
    margin-bottom: 0;
  }

  .status {
    color: #777;
    font-size: 1rem !important;
    font-weight: 600;
    text-align: right;

    span.active {
      color: #28a745;
    }

    span.pending {
      color: #ffc107;
    }

    span.rejected {
      color: #dc3545;
    }
  }

  .cv-avatar {
    width: 100px;
    height: 100px;
    border-radius: 50%;
  }

  .cif {
    color: #999;
    font-weight: 600;
    font-size: 1.2rem;

    span {
      font-size: 1rem;
    }
  }

  .description {
    margin: 0.6rem 0;
    font-size: 0.9rem;
    white-space: pre-line
  }

  .sm-text {
    font-size: 0.9rem;
    color: #666;
  }
</style>
